/**
 * Panel-surface renderers — the artifact rendered DIRECTLY on the panel paper (no card wrapper). Registry keyed
 * by type with a mandatory fallback for unknown kinds. Reuse:
 *   table → ai-teammate `TableArtifact` (`variant="dashboard"`: the full data grid, no preview/pin chrome)
 *   plot  → `PlotlyChart` (what ai-teammate's plot-artifact renders; chrome tracks the active color scheme)
 * text/markdown are typography/markdown. HTML is never rendered from markup we hold: a file-backed
 * HTML artifact loads as its own document from pi-server's panel route, and the inline `html` kind
 * shows its markup as text (see the registry).
 */

import { memo, useEffect, useMemo, useRef, useState } from "react"
import type { ComponentType, ReactNode } from "react"
import { Box, Button, Skeleton, Typography, styled } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { ErrorBoundary } from "@sentry/react"
import PlotlyChartImpl from "components/plotly-chart"
import TableArtifactImpl from "connected-components/ai-teammate/components/table-artifact"
import { getText } from "api/get-text"
import { useSelector } from "react-redux"
import { getNeedSignTerms } from "modules/account-root/selectors"
import { useAccountAlias, useUserId } from "resources/account"
import { formatArtifactSize } from "helpers/formatters/file-size"
import {
  type ArtifactEnvelope,
  type KnownArtifact,
  type PlotFigure,
  type TableArtifact,
  artifactMediaType,
  isFileArtifact,
  isMarkdownArtifact,
  isPlotArtifact,
  isTableArtifact,
  isTextArtifact,
  normalizeFileArtifact,
} from "./artifact-contract"
import { ChatMarkdown } from "./chat-markdown"
import { serializeArtifactData } from "./artifact-serialize"
import { buildArtifactUrl } from "./artifact-content"
import { getArtifactRenderUrl } from "api/get-artifact-render-url"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { useArtifactDownload } from "./use-artifact-download"
import { useArtifactFileBroker } from "./use-artifact-file-broker"
import type { BrokerRefusal } from "./use-artifact-file-broker"
import { saveArtifactFile } from "./save-artifact-file"
import { ArtifactRefusalNotice, ArtifactSaveDialog } from "./artifact-save-dialog"
import type { PendingSave } from "./artifact-save-request"

// Typed views over untyped-JS components (their PropTypes-only signatures otherwise infer every prop as required).
const TableArtifactView = TableArtifactImpl as ComponentType<{ artifact: TableArtifact, variant?: "chat" | "dashboard" }>
const PlotlyChart = PlotlyChartImpl as ComponentType<{ data: PlotFigure["data"], layout: Record<string, unknown>, className?: string }>

interface PanelRendererProps {
  artifact: ArtifactEnvelope
  /** Docked (false) caps the plot/table to readable proportions; expanded (true) fills the region. */
  isExpanded: boolean
  threadId?: string | null
}

type PanelRenderer = ComponentType<PanelRendererProps>

const FILL_SX = { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } satisfies SxProps<Theme>
// Direct wrapper sizes the chart: Plotly's autosize needs a measured container.
const PLOT_SX = { flex: 1, minHeight: "20rem", "& .plotly-chart": { height: "100%" } } satisfies SxProps<Theme>
// Docked: a fixed, ~4:3-ish chart height rather than filling the tall panel column.
const PLOT_DOCKED_SX = { flexShrink: 0, height: "28rem", "& .plotly-chart": { height: "100%" } } satisfies SxProps<Theme>
// Docked: cap the grid height and let it scroll internally instead of stretching the full column.
const TABLE_DOCKED_SX = { flex: 1, minHeight: "12rem", maxHeight: "26rem", display: "flex", flexDirection: "column" } satisfies SxProps<Theme>
const TEXT_SX = { whiteSpace: "pre-wrap", wordBreak: "break-word" } satisfies SxProps<Theme>
const ERROR_NOTICE_SX = { display: "block", mb: 1, color: "text.secondary" } satisfies SxProps<Theme>
const MARKDOWN_OPTIONS = { disableParsingRawHTML: true }
const PROSE_SX = { "& > :first-child": { mt: 0 }, "& > :last-child": { mb: 0 } } satisfies SxProps<Theme>

const HtmlFrame = styled("iframe")(({ theme }) => ({
  flex: 1,
  minHeight: "20rem",
  width: "100%",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}))

function TablePanel({ artifact, isExpanded }: PanelRendererProps) {
  if (!isTableArtifact(artifact)) return <FallbackPanel artifact={artifact} isExpanded={isExpanded} />
  return (
    <Box sx={isExpanded ? FILL_SX : TABLE_DOCKED_SX} data-testid="chatbox-artifact-panel-table">
      <TableArtifactView artifact={artifact} variant="dashboard" />
    </Box>
  )
}

function PlotPanel({ artifact, isExpanded }: PanelRendererProps) {
  if (!isPlotArtifact(artifact)) return <FallbackPanel artifact={artifact} isExpanded={isExpanded} />
  const { data, layout } = artifact.figure
  return (
    <Box sx={isExpanded ? PLOT_SX : PLOT_DOCKED_SX} data-testid="chatbox-artifact-panel-plot">
      <PlotlyChart data={data} layout={layout ?? {}} />
    </Box>
  )
}

function TextPanel({ artifact, isExpanded }: PanelRendererProps) {
  if (!isTextArtifact(artifact)) return <FallbackPanel artifact={artifact} isExpanded={isExpanded} />
  return (
    <Typography variant="body2" sx={TEXT_SX} data-testid="chatbox-artifact-panel-text">
      {artifact.text}
    </Typography>
  )
}

function MarkdownPanel({ artifact, isExpanded }: PanelRendererProps) {
  if (!isMarkdownArtifact(artifact)) return <FallbackPanel artifact={artifact} isExpanded={isExpanded} />
  return (
    <Box sx={PROSE_SX} data-testid="chatbox-artifact-panel-markdown">
      <ChatMarkdown options={MARKDOWN_OPTIONS}>{artifact.markdown}</ChatMarkdown>
    </Box>
  )
}

const HTML_MEDIA_TYPE = "text/html"
const MARKDOWN_MEDIA_TYPE = "text/markdown"
const PLAIN_TEXT_MEDIA_TYPES = ["text/csv", "text/plain"]

const FILE_SX = { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1 } satisfies SxProps<Theme>
const FILE_META_SX = { display: "flex", flexDirection: "column", gap: 0.5 } satisfies SxProps<Theme>
const FILE_DOWNLOAD_SX = { alignSelf: "flex-start" } satisfies SxProps<Theme>
const FILE_SKELETON_SX = { flex: 1, minHeight: "10rem" } satisfies SxProps<Theme>

type FrameUrlState =
  | { key: string, status: "loading" }
  | { key: string, status: "loaded", url: string }
  | { key: string, status: "error", httpStatus: number }

function loadingFrameUrlState(key: string): FrameUrlState {
  return { key, status: "loading" }
}

type FileContentState =
  | { key: string, status: "loading" }
  | { key: string, status: "loaded", text: string }
  | { key: string, status: "error", httpStatus: number }

function loadingFileContentState(key: string): FileContentState {
  return { key, status: "loading" }
}

function fileContentUnavailableMessage(httpStatus: number): string {
  if (httpStatus === 404) return "This file is no longer available."
  if (httpStatus === 413) return "This file is too large to preview. Download it instead."
  return "This file couldn't be previewed."
}

function FilePanel({ artifact, isExpanded, threadId }: PanelRendererProps) {
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const needSignTerms = useSelector(getNeedSignTerms)
  const needSignTermsRef = useRef(needSignTerms)
  useEffect(() => {
    needSignTermsRef.current = needSignTerms
  }, [needSignTerms])
  const fileArtifact = isFileArtifact(artifact) ? artifact : null
  const { downloadArtifact, isDownloading } = useArtifactDownload(fileArtifact, threadId)
  const analytics = useAnalytics()

  // An artifact cannot save a file itself — its sandbox has no allow-downloads (APP-3495). It asks
  // instead, and the broker below holds that request until the user has seen what it would write.
  const frameElementRef = useRef<HTMLIFrameElement | null>(null)
  const trackExport = (event: string, properties: Record<string, unknown>) => {
    analytics.track(event, { artifactId: fileArtifact?.id, ...(threadId != null && { threadId }), ...properties })
  }
  const { pending, refusal, confirmSave, cancelSave, dismissRefusal } = useArtifactFileBroker({
    frameRef: frameElementRef,
    onSaved: (save: PendingSave) => {
      saveArtifactFile(save)
      trackExport(AI_TEAMMATE_EVENTS.ARTIFACT_EXPORT_SAVED, { mediaType: save.contentType, sizeBytes: save.sizeBytes })
    },
    onRefused: (reason: BrokerRefusal) => trackExport(AI_TEAMMATE_EVENTS.ARTIFACT_EXPORT_REFUSED, { reason }),
  })

  const normalized = fileArtifact ? normalizeFileArtifact(fileArtifact) : null
  const filename = normalized?.filename ?? ""
  const contentType = normalized?.contentType ?? ""
  const sizeBytes = normalized?.sizeBytes
  const mediaType = artifactMediaType(contentType)
  const canBuildUrl = fileArtifact != null && accountAlias != null && threadId != null

  // HTML must load as its own document, not through /content: injected markup has no policy
  // container of its own and inherits the app shell's CSP, under which no script in it runs. The
  // URL is minted by the server rather than built here, because it carries the ticket that
  // authorizes the render — so this branch is asynchronous where the others are not.
  const isHtml = canBuildUrl && mediaType === HTML_MEDIA_TYPE
  const renderApiUrl = isHtml ? buildArtifactUrl({ accountAlias, threadId, artifactId: fileArtifact.id }, "render-url") : null
  const [frameState, setFrameState] = useState<FrameUrlState>(() => loadingFrameUrlState(renderApiUrl ?? ""))

  useEffect(() => {
    if (renderApiUrl == null) return undefined
    let cancelled = false
    getArtifactRenderUrl(renderApiUrl, { account: accountAlias, userId, getNeedSignTerms: () => needSignTermsRef.current }).then((result) => {
      if (cancelled) return
      setFrameState(result.kind === "url"
        ? { key: renderApiUrl, status: "loaded", url: result.url }
        : { key: renderApiUrl, status: "error", httpStatus: result.status })
    })
    return () => {
      cancelled = true
    }
  }, [renderApiUrl, accountAlias, userId])

  // Same keying as the content fetch: a URL minted for the previous artifact must never paint here.
  const currentFrameState = frameState.key === (renderApiUrl ?? "") ? frameState : loadingFrameUrlState(renderApiUrl ?? "")

  // The media types still served as text through /content. HTML is deliberately absent.
  const fetchesContent = mediaType === MARKDOWN_MEDIA_TYPE || PLAIN_TEXT_MEDIA_TYPES.includes(mediaType)

  const url = canBuildUrl && fetchesContent
    ? buildArtifactUrl({ accountAlias, threadId, artifactId: fileArtifact.id }, "content")
    : null

  const [contentState, setContentState] = useState<FileContentState>(() => loadingFileContentState(url ?? ""))

  useEffect(() => {
    if (url == null) return undefined
    let cancelled = false
    getText(url, { account: accountAlias, userId, getNeedSignTerms: () => needSignTermsRef.current }).then((result) => {
      if (cancelled) return
      if (result.kind === "text") {
        setContentState({ key: url, status: "loaded", text: result.text })
      } else {
        setContentState({ key: url, status: "error", httpStatus: result.status })
      }
    })
    return () => {
      cancelled = true
    }
  }, [url, accountAlias, userId])

  const currentContentState = contentState.key === (url ?? "") ? contentState : loadingFileContentState(url ?? "")

  if (fileArtifact == null) return <FallbackPanel artifact={artifact} isExpanded={isExpanded} />

  const onDownloadClick = () => {
    if (threadId == null) return
    downloadArtifact(threadId, fileArtifact.id)
  }

  const downloadButton = (
    <Button
      size="small"
      variant="outlined"
      aria-label={`Download ${filename}`}
      sx={FILE_DOWNLOAD_SX}
      disabled={threadId == null || isDownloading}
      onClick={onDownloadClick}
      data-testid="chatbox-artifact-panel-file-download"
    >
      Download
    </Button>
  )

  const formattedSize = sizeBytes === undefined ? "" : formatArtifactSize(sizeBytes)
  const metadata = (
    <Box sx={FILE_META_SX}>
      <Typography variant="body2">{filename}</Typography>
      <Typography variant="caption" color="text.secondary">
        {[contentType, formattedSize].filter((part) => part !== "").join(" · ")}
      </Typography>
    </Box>
  )

  let body: ReactNode
  if (isHtml) {
    if (currentFrameState.status === "loading") {
      body = <Skeleton variant="rectangular" sx={FILE_SKELETON_SX} data-testid="chatbox-artifact-panel-file-loading" />
    } else if (currentFrameState.status === "error") {
      body = (
        <>
          {metadata}
          <Typography variant="body2" color="text.secondary">{fileContentUnavailableMessage(currentFrameState.httpStatus)}</Typography>
        </>
      )
    } else {
      // A changed key unmounts the old iframe and mounts a fresh one. Reusing the node and only
      // swapping src would navigate that iframe, which adds a browser-history entry — the user's
      // next Back press would step backwards inside the panel instead of leaving the page.
      body = (
        <HtmlFrame
          key={currentFrameState.url}
          ref={frameElementRef}
          title={filename}
          src={currentFrameState.url}
          referrerPolicy="no-referrer"
          allow=""
          data-testid="chatbox-artifact-panel-file-html"
        />
      )
    }
  } else if (url != null) {
    if (currentContentState.status === "loading") {
      body = <Skeleton variant="rectangular" sx={FILE_SKELETON_SX} data-testid="chatbox-artifact-panel-file-loading" />
    } else if (currentContentState.status === "error") {
      body = (
        <>
          {metadata}
          <Typography variant="body2" color="text.secondary">{fileContentUnavailableMessage(currentContentState.httpStatus)}</Typography>
        </>
      )
    } else if (PLAIN_TEXT_MEDIA_TYPES.includes(mediaType)) {
      body = (
        <Typography variant="body2" sx={TEXT_SX} data-testid="chatbox-artifact-panel-file-text">
          {currentContentState.text}
        </Typography>
      )
    } else {
      body = (
        <Box sx={PROSE_SX} data-testid="chatbox-artifact-panel-file-markdown">
          <ChatMarkdown options={MARKDOWN_OPTIONS}>{currentContentState.text}</ChatMarkdown>
        </Box>
      )
    }
  } else {
    body = metadata
  }

  return (
    <Box sx={FILE_SX} data-testid="chatbox-artifact-panel-file">
      {body}
      {downloadButton}
      <ArtifactSaveDialog pending={pending} onConfirm={confirmSave} onCancel={cancelSave} />
      <ArtifactRefusalNotice refusal={refusal} onDismiss={dismissRefusal} />
    </Box>
  )
}

/** Fallback for unknown kinds (and known kinds whose payload fails its shape guard) — the values as plain text. */
function FallbackPanel({ artifact }: PanelRendererProps) {
  const serialized = useMemo(() => serializeArtifactData(artifact), [artifact])
  return (
    <Typography variant="body2" component="pre" sx={TEXT_SX} data-testid="chatbox-artifact-panel-fallback">
      {serialized}
    </Typography>
  )
}

// Open lookup for the panel surface; the `satisfies` check keeps the known kinds exhaustive (a new
// KnownArtifact member must register a renderer or fail to compile).
const PANEL_RENDERERS: Partial<Record<string, PanelRenderer>> = {
  table: TablePanel,
  plot: PlotPanel,
  text: TextPanel,
  markdown: MarkdownPanel,
  // Markup as text, deliberately: injected HTML inherits the shell's policy, which runs no script
  // but still allows an <img> to any https host — egress with no JavaScript. Nothing emits this
  // kind today; a future producer should serve a document through the panel route, as `file` does.
  html: FallbackPanel,
  file: FilePanel,
} satisfies Record<KnownArtifact["type"], PanelRenderer>

/** Explains the failure before showing the serialized data — without the notice it would be indistinguishable
 *  from the unknown-kind fallback. Exported for the Storybook story; in the app the error boundary renders it. */
export function ArtifactErrorPanel({ artifact, isExpanded, threadId }: PanelRendererProps) {
  return (
    <Box sx={FILL_SX} data-testid="chatbox-artifact-panel-error">
      <Typography variant="caption" sx={ERROR_NOTICE_SX}>
        This artifact couldn&rsquo;t be displayed &mdash; showing its raw data.
      </Typography>
      <FallbackPanel artifact={artifact} isExpanded={isExpanded} threadId={threadId} />
    </Box>
  )
}

/** Render an artifact on the panel surface, routing unknown kinds to the fallback. A renderer that throws
 *  degrades to that same serialized view behind a notice rather than unmounting the whole surface. */
function ArtifactPanelBodyComponent({ artifact, isExpanded, threadId }: PanelRendererProps) {
  const Renderer = PANEL_RENDERERS[artifact.type] ?? FallbackPanel
  // Function form so the fallback JSX is only built if an error occurred.
  const renderFallback = () => <ArtifactErrorPanel artifact={artifact} isExpanded={isExpanded} threadId={threadId} />
  return (
    <Box sx={FILL_SX}>
      <ErrorBoundary fallback={renderFallback}>
        <Renderer artifact={artifact} isExpanded={isExpanded} threadId={threadId} />
      </ErrorBoundary>
    </Box>
  )
}

export const ArtifactPanelBody = memo(ArtifactPanelBodyComponent)
